import { LAYOUT_CONSTANTS } from './types';

interface ConnectorProps {
  parentX: number;
  parentY: number;
  parentWidth: number;
  parentHeight: number;
  childX: number;
  childY: number;
  childWidth: number;
}

/**
 * Renders an SVG path connecting a parent node to a child node.
 * Uses a "step" connector style:
 *   - Vertical line down from parent center-bottom
 *   - Horizontal line to align with child
 *   - Vertical line down to child center-top
 */
export function OrgChartConnector({
  parentX,
  parentY,
  parentWidth,
  parentHeight,
  childX,
  childY,
  childWidth,
}: ConnectorProps) {
  // Parent connection point (center-bottom)
  const startX = parentX + parentWidth / 2;
  const startY = parentY + parentHeight;

  // Child connection point (center-top)
  const endX = childX + childWidth / 2;
  const endY = childY;

  // Midpoint for horizontal line
  const midY = startY + LAYOUT_CONSTANTS.VERTICAL_GAP / 2;

  // Create path: down, across, down
  const path = `
    M ${startX} ${startY}
    L ${startX} ${midY}
    L ${endX} ${midY}
    L ${endX} ${endY}
  `;

  return (
    <path
      d={path}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      className="text-border"
    />
  );
}

interface ConnectorGroupProps {
  parentX: number;
  parentY: number;
  parentWidth: number;
  parentHeight: number;
  children: Array<{
    x: number;
    y: number;
    width: number;
  }>;
}

/**
 * Renders connectors from a single parent to multiple children.
 * Optimized to draw a single horizontal bar with drops to each child.
 */
export function OrgChartConnectorGroup({
  parentX,
  parentY,
  parentWidth,
  parentHeight,
  children,
}: ConnectorGroupProps) {
  if (children.length === 0) return null;

  // Parent connection point (center-bottom)
  const startX = parentX + parentWidth / 2;
  const startY = parentY + parentHeight;

  // Midpoint for horizontal bar
  const midY = startY + LAYOUT_CONSTANTS.VERTICAL_GAP / 2;

  // Calculate horizontal bar extent
  const childCenters = children.map((c) => c.x + c.width / 2);
  const leftMost = Math.min(...childCenters);
  const rightMost = Math.max(...childCenters);

  // Build the path
  let path = `M ${startX} ${startY} L ${startX} ${midY}`;

  // If multiple children, draw horizontal bar
  if (children.length > 1) {
    path += ` M ${leftMost} ${midY} L ${rightMost} ${midY}`;
  }

  // Add drops to each child
  children.forEach((child) => {
    const childCenterX = child.x + child.width / 2;
    const childTopY = child.y;
    path += ` M ${childCenterX} ${midY} L ${childCenterX} ${childTopY}`;
  });

  return (
    <path
      d={path}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      className="text-border"
      strokeLinecap="round"
    />
  );
}

export default OrgChartConnector;
